import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './WordSearch.css';

interface Word {
  word: string;
  found: boolean;
  foundByUser: boolean; // true if found by user, false if revealed
}

const WordSearch: React.FC = () => {
  const { t } = useTranslation();
  const words: string[] = t('wordSearch.words', { returnObjects: true }) as string[];

  const [grid, setGrid] = useState<string[][]>([]);
  const [selectedCells, setSelectedCells] = useState<number[][]>([]);
  const [foundWords, setFoundWords] = useState<Word[]>(words.map(word => ({ word, found: false, foundByUser: false })));
  const [foundWordPositions, setFoundWordPositions] = useState<number[][]>([]);
  const [wordPositionColors, setWordPositionColors] = useState<Map<string, number>>(new Map());
  const [isSelecting, setIsSelecting] = useState(false);
  const [startCell, setStartCell] = useState<number[] | null>(null);
  const [firstClickCell, setFirstClickCell] = useState<number[] | null>(null);
  const [waitingForSecondClick, setWaitingForSecondClick] = useState(false);
  const [loading, setLoading] = useState(true);
  const [solutionRevealed, setSolutionRevealed] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [finalTime, setFinalTime] = useState<string>('');
  const [hintCount, setHintCount] = useState<number>(0);
  const [hintingCells, setHintingCells] = useState<Set<string>>(new Set());

  // Array of subtle green and grey shades for found words
  const foundWordColors = [
    'found-word-1', // Dark green
    'found-word-2', // Forest green
    'found-word-3', // Olive green
    'found-word-4', // Slate green
    'found-word-5', // Sea green
    'found-word-6', // Pine green
    'found-word-7', // Sage green
    'found-word-8', // Charcoal grey
    'found-word-9', // Steel grey
    'found-word-10', // Moss green
    'found-word-11', // Teal green
    'found-word-12', // Warm grey
    'found-word-13', // Cool grey
    'found-word-14', // Dark sage
    'found-word-15'  // Deep green
  ];

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start the game and timer
  const startGame = () => {
    setGameStarted(true);
    setStartTime(Date.now());
    setCurrentTime(0);
  };

  // Stop the timer and record final time
  const stopTimer = () => {
    if (startTime) {
      const endTime = Date.now();
      const totalSeconds = Math.floor((endTime - startTime) / 1000);
      setFinalTime(formatTime(totalSeconds));
      setCurrentTime(totalSeconds);
      setStartTime(null);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (gameStarted && startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setCurrentTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [gameStarted, startTime]);

  // Check for game completion
  useEffect(() => {
    if (gameStarted && !solutionRevealed && foundWords.every(w => w.found)) {
      stopTimer();
    }
  }, [foundWords, gameStarted, solutionRevealed]);

  // Generate the word search grid
  useEffect(() => {
    const generateGrid = () => {
      const size = 15; // 15x15 grid
      const newGrid: string[][] = Array(size).fill(null).map(() => Array(size).fill(''));
      
      // Sort words by length (longer words first) for better placement
      const sortedWords = [...words].sort((a, b) => b.length - a.length);
      
      // Place words in the EMPTY grid first
      const placedWords: string[] = [];
      
      sortedWords.forEach(word => {
        let placed = false;
        let attempts = 0;
        const maxAttempts = 1000; // Increased attempts since grid starts empty
        
        while (!placed && attempts < maxAttempts) {
          const direction = Math.floor(Math.random() * 8); // 8 possible directions
          const row = Math.floor(Math.random() * size);
          const col = Math.floor(Math.random() * size);
          
          if (canPlaceWord(newGrid, word, row, col, direction)) {
            placeWord(newGrid, word, row, col, direction);
            placed = true;
            placedWords.push(word);
          }
          attempts++;
        }
        
        if (!placed) {
          console.warn(`Could not place word: ${word} after ${maxAttempts} attempts`);
        }
      });

      console.log(`Successfully placed ${placedWords.length} out of ${words.length} words:`, placedWords);

      // NOW fill remaining empty cells with random letters
      for (let i = 0; i < size; i++) {
        for (let j = 0; j < size; j++) {
          if (newGrid[i][j] === '') {
            newGrid[i][j] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
          }
        }
      }

      return newGrid;
    };

    // Add a small delay to prevent blocking the UI
    const timer = setTimeout(() => {
      const newGrid = generateGrid();
      setGrid(newGrid);
      setLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const canPlaceWord = (grid: string[][], word: string, row: number, col: number, direction: number): boolean => {
    const size = grid.length;
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];

    const [dx, dy] = directions[direction];
    
    for (let i = 0; i < word.length; i++) {
      const newRow = row + i * dx;
      const newCol = col + i * dy;
      
      if (newRow < 0 || newRow >= size || newCol < 0 || newCol >= size) {
        return false;
      }
      
      // Allow placement if cell is empty or contains the same letter
      if (grid[newRow][newCol] !== '' && grid[newRow][newCol] !== word[i]) {
        return false;
      }
    }
    
    return true;
  };

  const placeWord = (grid: string[][], word: string, row: number, col: number, direction: number) => {
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];

    const [dx, dy] = directions[direction];
    
    for (let i = 0; i < word.length; i++) {
      grid[row + i * dx][col + i * dy] = word[i];
    }
  };

  // Check if a cell is the starting position of any unfound word
  const findWordFromPosition = (row: number, col: number): { word: string, positions: number[][] } | null => {
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];

    // Check each unfound word
    for (const wordObj of foundWords) {
      if (wordObj.found) continue; // Skip already found words
      
      const word = wordObj.word;
      
      // Check each direction
      for (let dirIndex = 0; dirIndex < directions.length; dirIndex++) {
        const [dx, dy] = directions[dirIndex];
        let matches = true;
        const positions: number[][] = [];
        
        // Check if the word exists starting from this position in this direction
        for (let i = 0; i < word.length; i++) {
          const newRow = row + i * dx;
          const newCol = col + i * dy;
          
          // Check bounds
          if (newRow < 0 || newRow >= grid.length || newCol < 0 || newCol >= grid[0].length) {
            matches = false;
            break;
          }
          
          // Check if letter matches
          if (grid[newRow][newCol] !== word[i]) {
            matches = false;
            break;
          }
          
          positions.push([newRow, newCol]);
        }
        
        if (matches) {
          return { word, positions };
        }
      }
    }
    
    return null;
  };

  // Check if two positions form a valid word
  const findWordBetweenPositions = (startRow: number, startCol: number, endRow: number, endCol: number): { word: string, positions: number[][] } | null => {
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];

    // Calculate direction between start and end
    const dx = endRow - startRow;
    const dy = endCol - startCol;
    
    // Check if it's a valid straight line
    if (!(Math.abs(dx) === Math.abs(dy) || dx === 0 || dy === 0)) {
      return null; // Not a straight line
    }

    // Normalize direction
    const length = Math.max(Math.abs(dx), Math.abs(dy)) + 1;
    const stepX = dx === 0 ? 0 : dx / Math.abs(dx);
    const stepY = dy === 0 ? 0 : dy / Math.abs(dy);

    // Build the word from start to end
    const positions: number[][] = [];
    let wordForward = '';
    let wordBackward = '';
    
    for (let i = 0; i < length; i++) {
      const row = startRow + i * stepX;
      const col = startCol + i * stepY;
      
      if (row < 0 || row >= grid.length || col < 0 || col >= grid[0].length) {
        return null;
      }
      
      positions.push([row, col]);
      wordForward += grid[row][col];
      wordBackward = grid[row][col] + wordBackward;
    }

    // Check if either direction matches an unfound word
    for (const wordObj of foundWords) {
      if (wordObj.found) continue;
      
      if (wordObj.word === wordForward || wordObj.word === wordBackward) {
        return { word: wordObj.word, positions };
      }
    }
    
    return null;
  };

  // Check if a cell is the starting position of any unfound word (for visual hints)
  const isWordStart = (row: number, col: number): boolean => {
    return findWordFromPosition(row, col) !== null;
  };

  // Find first letters of all unfound words
  const findUnfoundWordStarts = (): number[][] => {
    const startPositions: number[][] = [];
    const foundStarts = new Set<string>();

    // Get list of unfound words
    const unfoundWords = foundWords.filter(w => !w.found).map(w => w.word);

    // Scan grid for starting positions of unfound words
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[0].length; col++) {
        const wordData = findWordFromPosition(row, col);
        if (wordData && unfoundWords.includes(wordData.word)) {
          const posKey = `${row}-${col}`;
          if (!foundStarts.has(posKey)) {
            startPositions.push([row, col]);
            foundStarts.add(posKey);
          }
        }
      }
    }

    return startPositions;
  };

  // Show hint by flashing first letters of unfound words
  const showHint = () => {
    if (!gameStarted || solutionRevealed) return;

    setHintCount(prev => prev + 1);
    const unfoundStarts = findUnfoundWordStarts();
    
    // Add hinting cells to state
    const hintCellKeys = new Set(unfoundStarts.map(([r, c]) => `${r}-${c}`));
    setHintingCells(hintCellKeys);

    // Remove hinting state after animation completes (5.5 seconds total)
    setTimeout(() => {
      setHintingCells(new Set());
    }, 5500);
  };

  // Reveal all unfound words
  const revealSolution = () => {
    const allWordPositions: number[][] = [];
    const revealedWordData: { word: string, positions: number[][], colorIndex: number }[] = [];
    let colorIndex = foundWords.filter(w => w.found).length; // Start after already found words
    
    // Find all unfound words in the grid
    foundWords.forEach(wordObj => {
      if (!wordObj.found) {
        // Search for this word in the grid
        for (let row = 0; row < grid.length; row++) {
          for (let col = 0; col < grid[0].length; col++) {
            const wordData = findWordFromPosition(row, col);
            if (wordData && wordData.word === wordObj.word) {
              allWordPositions.push(...wordData.positions);
              revealedWordData.push({
                word: wordData.word,
                positions: wordData.positions,
                colorIndex: colorIndex % foundWordColors.length
              });
              colorIndex++;
              return; // Found this word, move to next
            }
          }
        }
      }
    });

    // Mark all words as found but distinguish user-found vs revealed
    setFoundWords(prev => 
      prev.map(w => ({
        ...w,
        found: true,
        foundByUser: w.foundByUser // Keep original foundByUser state
      }))
    );

    // Add all word positions to highlighting
    setFoundWordPositions(prev => [...prev, ...allWordPositions]);
    
    // Map each revealed word position to its color
    setWordPositionColors(prev => {
      const newMap = new Map(prev);
      revealedWordData.forEach(wordData => {
        wordData.positions.forEach(([r, c]) => {
          newMap.set(`${r}-${c}`, wordData.colorIndex);
        });
      });
      return newMap;
    });
    
    // Mark solution as revealed and disable interaction
    setSolutionRevealed(true);
    setIsSelecting(false);
    setFirstClickCell(null);
    setWaitingForSecondClick(false);
    setSelectedCells([]);
    setStartCell(null);
    
    // Stop the timer
    stopTimer();
    
    console.log('Solution revealed! Game ended.');
  };

  const handleCellMouseDown = (row: number, col: number) => {
    // Disable interaction if solution is revealed or game not started
    if (solutionRevealed || !gameStarted) return;
    
    // Check if this is a second click for two-click selection
    if (waitingForSecondClick && firstClickCell) {
      const [firstRow, firstCol] = firstClickCell;
      
      // Check if clicked the same cell (cancel selection)
      if (firstRow === row && firstCol === col) {
        setFirstClickCell(null);
        setWaitingForSecondClick(false);
        return;
      }
      
      // Check if the two clicks form a valid word
      const foundWordData = findWordBetweenPositions(firstRow, firstCol, row, col);
      
      if (foundWordData) {
        // Assign a color to this word
        const colorIndex = foundWords.filter(w => w.found).length % foundWordColors.length;
        const positionKey = foundWordData.positions.map(([r, c]) => `${r}-${c}`).join(',');
        
        // Mark word as found
        setFoundWords(prev => 
          prev.map(w => w.word === foundWordData.word ? { ...w, found: true, foundByUser: true } : w)
        );
        
        // Store the positions of the found word for permanent highlighting
        setFoundWordPositions(prev => [...prev, ...foundWordData.positions]);
        
        // Map each position to its word color
        setWordPositionColors(prev => {
          const newMap = new Map(prev);
          foundWordData.positions.forEach(([r, c]) => {
            newMap.set(`${r}-${c}`, colorIndex);
          });
          return newMap;
        });
        
        console.log(`Found word by two-click: ${foundWordData.word}!`);
      }
      
      // Reset two-click state
      setFirstClickCell(null);
      setWaitingForSecondClick(false);
      return;
    }
    
    // Start drag selection OR first click of two-click selection
    setIsSelecting(true);
    setStartCell([row, col]);
    setSelectedCells([[row, col]]);
  };

  const handleCellMouseEnter = (row: number, col: number) => {
    if (!isSelecting || !startCell || solutionRevealed || !gameStarted) return;

    // If we were waiting for second click but user started dragging, cancel two-click mode
    if (waitingForSecondClick) {
      setFirstClickCell(null);
      setWaitingForSecondClick(false);
    }

    const [startRow, startCol] = startCell;
    const newSelectedCells: number[][] = [];
    
    // Determine direction
    const dx = row - startRow;
    const dy = col - startCol;
    
    // Only allow straight lines (horizontal, vertical, or diagonal)
    if (Math.abs(dx) === Math.abs(dy) || dx === 0 || dy === 0) {
      const length = Math.max(Math.abs(dx), Math.abs(dy)) + 1;
      const stepX = dx === 0 ? 0 : dx / Math.abs(dx);
      const stepY = dy === 0 ? 0 : dy / Math.abs(dy);
      
      for (let i = 0; i < length; i++) {
        newSelectedCells.push([
          startRow + i * stepX,
          startCol + i * stepY
        ]);
      }
    }
    
    setSelectedCells(newSelectedCells);
  };

  // Touch support for mobile devices
  const handleCellTouchStart = (row: number, col: number, e: React.TouchEvent) => {
    if (solutionRevealed || !gameStarted) return;
    e.preventDefault();
    handleCellMouseDown(row, col);
  };

  const handleCellTouchMove = (e: React.TouchEvent) => {
    if (solutionRevealed || !gameStarted) return;
    e.preventDefault();
    if (!isSelecting) return;
    
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.classList.contains('grid-cell')) {
      const rowIndex = parseInt(element.getAttribute('data-row') || '0');
      const colIndex = parseInt(element.getAttribute('data-col') || '0');
      handleCellMouseEnter(rowIndex, colIndex);
    }
  };

  const handleCellTouchEnd = (e: React.TouchEvent) => {
    if (solutionRevealed || !gameStarted) return;
    e.preventDefault();
    handleCellMouseUp();
  };

  const handleCellMouseUp = () => {
    if (!isSelecting || solutionRevealed || !gameStarted) return;
    
    setIsSelecting(false);
    
    // Check if this was a single click (no dragging)
    const isSingleClick = selectedCells.length === 1;
    
    if (isSingleClick && startCell) {
      // Single click behavior - start two-click selection
      setFirstClickCell(startCell);
      setWaitingForSecondClick(true);
      console.log('First click registered. Click on the last letter of the word to find it.');
      
      // Clear drag selection
      setSelectedCells([]);
      setStartCell(null);
      return;
    }
    
    // Existing drag selection behavior
    const selectedWord = selectedCells
      .map(([row, col]) => grid[row][col])
      .join('');
    
    const reversedWord = selectedWord.split('').reverse().join('');
    
    const foundWord = foundWords.find(w => 
      !w.found && (w.word === selectedWord || w.word === reversedWord)
    );
    
    if (foundWord) {
      // Assign a color to this word
      const colorIndex = foundWords.filter(w => w.found).length % foundWordColors.length;
      
      // Mark word as found
      setFoundWords(prev => 
        prev.map(w => w.word === foundWord.word ? { ...w, found: true, foundByUser: true } : w)
      );
      
      // Store the positions of the found word for permanent highlighting
      setFoundWordPositions(prev => [...prev, ...selectedCells]);
      
      // Map each position to its word color
      setWordPositionColors(prev => {
        const newMap = new Map(prev);
        selectedCells.forEach(([r, c]) => {
          newMap.set(`${r}-${c}`, colorIndex);
        });
        return newMap;
      });
      
      console.log(`Found word by dragging: ${foundWord.word}!`);
    }
    
    setSelectedCells([]);
    setStartCell(null);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="word-search-container">
        <h2>{t('wordSearch.title')}</h2>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('wordSearch.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="word-search-container">
      <h2>{t('wordSearch.title')}</h2>
      <p>{t('wordSearch.subtitle')}</p>
      
      {waitingForSecondClick && (
        <div className="second-click-hint">
          💡 Now click on the last letter of the word you found!
        </div>
      )}
      
      <div className="progress-info">
        <span className="progress-text">
          {t('wordSearch.foundWords')}: {foundWords.filter(w => w.found).length} / {foundWords.length} words
        </span>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ 
              width: `${(foundWords.filter(w => w.found).length / foundWords.length) * 100}%` 
            }}
          />
        </div>
        {gameStarted && (
          <div className="timer-display">
            ⏱️ Time: {formatTime(currentTime)}
          </div>
        )}
        {hintCount > 0 && (
          <div className="hint-count-display">
            💡 Hints used: {hintCount}
          </div>
        )}
      </div>
      
      {foundWords.filter(w => w.found).length === foundWords.length && (
        <div className="completion-celebration">
          {solutionRevealed 
            ? `🔍 Solution revealed! You found ${foundWords.filter(w => w.foundByUser).length} out of ${foundWords.length} words yourself${finalTime ? ` in ${finalTime}` : ''}.`
            : `🎉 ${t('wordSearch.congratulations')} ${t('wordSearch.allWordsFound')}${finalTime ? ` in ${finalTime}` : ''}! 🌱`
          }
        </div>
      )}
      
      <div className="word-list">
        {foundWords.map((word, index) => (
          <span 
            key={index} 
            className={`word ${word.found ? (word.foundByUser ? 'found' : 'not-found') : ''}`}
          >
            {word.word}
          </span>
        ))}
      </div>

      {!solutionRevealed && foundWords.filter(w => w.found).length < foundWords.length && gameStarted && (
        <div className="reveal-solution-container">
          <button 
            className="show-hint-btn"
            onClick={showHint}
          >
            💡 Show Hint
          </button>
          <button 
            className="reveal-solution-btn"
            onClick={revealSolution}
          >
            🔍 Reveal Solution
          </button>
        </div>
      )}

      <div className="grid-container-wrapper">
        {!gameStarted && (
          <div className="start-overlay">
            <div className="start-overlay-content">
              <h3>Ready to Start?</h3>
              <p>Find all eco-friendly words as fast as you can!</p>
              <button className="start-game-btn" onClick={startGame}>
                🎯 Start Word Search
              </button>
            </div>
          </div>
        )}
        
        <div className="grid-container">
          {grid.map((row, rowIndex) => (
            <div key={rowIndex} className="grid-row">
              {row.map((cell, colIndex) => {
                const isSelected = selectedCells.some(
                  ([r, c]) => r === rowIndex && c === colIndex
                );
                const isFoundWord = foundWordPositions.some(
                  ([r, c]) => r === rowIndex && c === colIndex
                );
                const isStartPosition = !isFoundWord && !solutionRevealed && gameStarted && isWordStart(rowIndex, colIndex);
                const isFirstClick = firstClickCell && 
                  firstClickCell[0] === rowIndex && firstClickCell[1] === colIndex;
                const isHinting = hintingCells.has(`${rowIndex}-${colIndex}`);
                
                // Get the color class for this position if it's a found word
                const positionKey = `${rowIndex}-${colIndex}`;
                const colorIndex = wordPositionColors.get(positionKey);
                const colorClass = isFoundWord && colorIndex !== undefined ? foundWordColors[colorIndex] : '';
                
                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`grid-cell ${isSelected ? 'selected' : ''} ${isFoundWord ? colorClass : ''} ${isStartPosition ? 'word-start' : ''} ${isFirstClick ? 'first-click' : ''} ${isHinting ? 'hinting' : ''}`}
                    data-row={rowIndex}
                    data-col={colIndex}
                    onMouseDown={() => handleCellMouseDown(rowIndex, colIndex)}
                    onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                    onMouseUp={handleCellMouseUp}
                    onTouchStart={(e) => handleCellTouchStart(rowIndex, colIndex, e)}
                    onTouchMove={handleCellTouchMove}
                    onTouchEnd={handleCellTouchEnd}
                  >
                    {cell}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WordSearch; 